import * as React from "react";

/**
 * Responsive image helper otimizado para mobile.
 * - Reescreve URLs do Pexels para pedir o tamanho certo (auto=compress).
 * - Reescreve URLs do Supabase Storage para usar o /render/image (transform).
 * - Gera srcSet + sizes para o navegador escolher o menor possível.
 * - Define decoding="async" e width/height para evitar CLS.
 */

type Props = Omit<React.ImgHTMLAttributes<HTMLImageElement>, "srcSet" | "sizes"> & {
  src: string;
  alt: string;
  /** Largura "natural" do slot na maior viewport (px). Default 800. */
  widths?: number[];
  sizes?: string;
  /** true para LCP — adiciona fetchpriority=high e loading=eager. */
  priority?: boolean;
};

const DEFAULT_WIDTHS = [320, 480, 640, 800, 1024, 1280];

function isPexels(u: string) {
  return /(^|\.)pexels\.com\//.test(u);
}

function isSupabasePublic(u: string) {
  return /\/storage\/v1\/object\/public\//.test(u);
}

function isLocalProductJpg(u: string) {
  return /^\/products\/[^/]+\.(jpe?g|png)$/i.test(u);
}

function withWidth(url: string, w: number): string {
  try {
    if (isPexels(url)) {
      const u = new URL(url);
      u.searchParams.set("auto", "compress");
      u.searchParams.set("cs", "tinysrgb");
      u.searchParams.set("w", String(w));
      u.searchParams.set("dpr", "1");
      return u.toString();
    }
    if (isSupabasePublic(url)) {
      const transformed = url.replace("/object/public/", "/render/image/public/");
      const u = new URL(transformed);
      u.searchParams.set("width", String(w));
      u.searchParams.set("quality", "70");
      u.searchParams.set("resize", "contain");
      u.searchParams.set("format", "webp");
      return u.toString();
    }
    if (isLocalProductJpg(url)) {
      // Pre-generated WebP variants live next to the JPG.
      return url.replace(/\.(jpe?g|png)$/i, ".webp");
    }
    return url;
  } catch {
    return url;
  }
}

function buildSrcSet(src: string, widths: number[]): string {
  return widths.map((w) => `${withWidth(src, w)} ${w}w`).join(", ");
}

export function OptImg({
  src,
  alt,
  widths = DEFAULT_WIDTHS,
  sizes = "(max-width: 640px) 92vw, (max-width: 1024px) 50vw, 800px",
  priority = false,
  loading,
  decoding,
  ...rest
}: Props) {
  if (!src) return null;
  const canTransform = isPexels(src) || isSupabasePublic(src) || isLocalProductJpg(src);
  // For local /products/*.jpg we have a single pre-rendered webp — no srcSet needed.
  const srcSet = canTransform && !isLocalProductJpg(src) ? buildSrcSet(src, widths) : undefined;
  const fallbackSrc = canTransform ? withWidth(src, widths[Math.floor(widths.length / 2)]) : src;

  return (
    <img
      {...rest}
      src={fallbackSrc}
      srcSet={srcSet}
      sizes={sizes}
      alt={alt}
      loading={priority ? "eager" : loading ?? "lazy"}
      decoding={decoding ?? "async"}
      fetchPriority={priority ? "high" : undefined}
    />
  );
}
